import { Mark, mergeAttributes } from '@tiptap/core';

const VALID_DIFF_OPS = new Set(['add', 'remove']);
const VALID_DIFF_STATUS = new Set(['pending', 'accepted', 'rejected']);
const VALID_DIFF_SOURCES = new Set(['ai_write', 'user_suggest', 'compare']);

const normalizeTextAttr = (value) => {
  const text = String(value || '').trim();
  return text || null;
};

const normalizeDiffOp = (value, fallback = 'add') => {
  const normalized = String(value || '').trim().toLowerCase();
  return VALID_DIFF_OPS.has(normalized) ? normalized : fallback;
};

const normalizeDiffStatus = (value, fallback = 'pending') => {
  const normalized = String(value || '').trim().toLowerCase();
  return VALID_DIFF_STATUS.has(normalized) ? normalized : fallback;
};

const normalizeDiffSource = (value, fallback = null) => {
  const normalized = String(value || '').trim().toLowerCase();
  return VALID_DIFF_SOURCES.has(normalized) ? normalized : fallback;
};

const normalizeTimestamp = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.floor(numeric);
};

const buildSharedDiffAttrs = (op) => ({
  annotation_id: {
    default: null,
    parseHTML: element => normalizeTextAttr(element?.getAttribute?.('data-diff-id')),
    renderHTML: attributes => {
      const value = normalizeTextAttr(attributes?.annotation_id);
      return value ? { 'data-diff-id': value } : {};
    }
  },
  status: {
    default: 'pending',
    parseHTML: element => normalizeDiffStatus(element?.getAttribute?.('data-diff-status'), 'pending'),
    renderHTML: () => ({})
  },
  source: {
    default: null,
    parseHTML: element => normalizeDiffSource(element?.getAttribute?.('data-diff-source'), null),
    renderHTML: () => ({})
  },
  author_id: {
    default: null,
    parseHTML: element => normalizeTextAttr(element?.getAttribute?.('data-diff-author-id')),
    renderHTML: () => ({})
  },
  created_at: {
    default: null,
    parseHTML: element => normalizeTimestamp(element?.getAttribute?.('data-diff-created-at')),
    renderHTML: () => ({})
  },
  batch_id: {
    default: null,
    parseHTML: element => normalizeTextAttr(element?.getAttribute?.('data-diff-batch-id')),
    renderHTML: attributes => {
      const value = normalizeTextAttr(attributes?.batch_id);
      return value ? { 'data-diff-batch-id': value } : {};
    }
  },
  diff_variant_of: {
    default: null,
    parseHTML: element => normalizeTextAttr(element?.getAttribute?.('data-diff-variant-of')),
    renderHTML: () => ({})
  },
  marker_type: {
    default: null,
    parseHTML: element => normalizeTextAttr(element?.getAttribute?.('data-diff-marker')),
    renderHTML: attributes => {
      const value = normalizeTextAttr(attributes?.marker_type);
      return value ? { 'data-diff-marker': value } : {};
    }
  },
  marker_direction: {
    default: null,
    parseHTML: element => normalizeTextAttr(element?.getAttribute?.('data-diff-direction')),
    renderHTML: attributes => {
      const value = normalizeTextAttr(attributes?.marker_direction);
      return value ? { 'data-diff-direction': value } : {};
    }
  },
  diff_op: {
    default: op,
    parseHTML: element => normalizeDiffOp(element?.getAttribute?.('data-diff-op'), op),
    renderHTML: attributes => ({
      'data-diff-op': normalizeDiffOp(attributes?.diff_op, op)
    })
  }
});

const buildRenderAttrs = (markType, HTMLAttributes = {}) => {
  const op = markType === 'remove' ? 'remove' : 'add';
  const filtered = {};

  const annotationId = normalizeTextAttr(HTMLAttributes?.['data-diff-id']);
  if (annotationId) filtered['data-diff-id'] = annotationId;

  const batchId = normalizeTextAttr(HTMLAttributes?.['data-diff-batch-id']);
  if (batchId) filtered['data-diff-batch-id'] = batchId;

  const markerType = normalizeTextAttr(HTMLAttributes?.['data-diff-marker']);
  if (markerType) filtered['data-diff-marker'] = markerType;

  const markerDirection = normalizeTextAttr(HTMLAttributes?.['data-diff-direction']);
  if (markerDirection) filtered['data-diff-direction'] = markerDirection;

  const classTokens = [
    'suggestion',
    op === 'remove' ? 'suggestion-remove' : 'suggestion-add'
  ];

  const incomingClass = String(HTMLAttributes?.class || '').trim();
  if (incomingClass) classTokens.push(incomingClass);

  return mergeAttributes(filtered, {
    class: classTokens.join(' ').trim(),
    'data-diff-op': op
  });
};

export const DiffSuggestionAddMark = Mark.create({
  name: 'diffSuggestionAdd',
  inclusive: false,

  addAttributes() {
    return buildSharedDiffAttrs('add');
  },

  parseHTML() {
    return [
      { tag: 'span[data-diff-op="add"]' },
      { tag: 'ins[data-diff-op="add"]' }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', buildRenderAttrs('add', HTMLAttributes), 0];
  }
});

export const DiffSuggestionRemoveMark = Mark.create({
  name: 'diffSuggestionRemove',
  inclusive: false,

  addAttributes() {
    return buildSharedDiffAttrs('remove');
  },

  parseHTML() {
    return [
      { tag: 'span[data-diff-op="remove"]' },
      { tag: 'del[data-diff-op="remove"]' }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', buildRenderAttrs('remove', HTMLAttributes), 0];
  }
});

const diffSuggestionMarks = [DiffSuggestionAddMark, DiffSuggestionRemoveMark];

export default diffSuggestionMarks;
